import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  HouseLine, Lightbulb, Fan, Thermometer, Lock, LockOpen,
  VideoCamera, Eye, Fire, Drop, Door, Plug, Shield,
  ShieldCheck, ShieldWarning, Siren, Plus, Trash, X,
  Power, Lightning, CaretRight, SlidersHorizontal, ArrowClockwise
} from '@phosphor-icons/react';
import { SceneShortcuts } from '@/components/SceneShortcuts';
import { SwipeableCard } from '@/components/SwipeableCard';

const DEVICE_ICONS = {
  sensor: Eye, camera: VideoCamera, appliance: Plug,
  door: Door, motion: Eye, smoke: Fire, water_leak: Drop,
  window: Door, glass_break: ShieldWarning, light: Lightbulb,
  fan: Fan, climate: Thermometer, lock: Lock,
};

const SECURITY_MODES = [
  { id: 'disarmed', label: 'Disarmed', color: '#737373', icon: Shield },
  { id: 'home', label: 'Home', color: '#22C55E', icon: ShieldCheck },
  { id: 'away', label: 'Away', color: '#FF6B35', icon: ShieldWarning },
  { id: 'night', label: 'Night', color: '#A855F7', icon: Shield },
];

export default function ControlCenterPage() {
  const { api } = useAuth();
  const [overview, setOverview] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDevices, setRoomDevices] = useState(null);
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockLoading, setLockLoading] = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      const [ovRes, lockRes] = await Promise.all([
        api.get('/control-center/overview'),
        api.get('/automation-rules/locks'),
      ]);
      setOverview(ovRes.data);
      setLocks(lockRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const fetchRoomDevices = async (roomId) => {
    try {
      const res = await api.get(`/control-center/rooms/${roomId}/devices`);
      setRoomDevices(res.data);
      setSelectedRoom(roomId);
    } catch (e) { console.error(e); }
  };

  const toggleLock = async (lockId) => {
    setLockLoading(lockId);
    try {
      await api.post(`/automation-rules/locks/${lockId}/toggle`);
      fetchOverview();
    } catch (e) { console.error(e); }
    setLockLoading('');
  };

  const triggerEmergency = async () => {
    if (!window.confirm('ACTIVATE EMERGENCY PROTOCOL?\n\nThis will lock all doors, turn on all lights, activate alarms, and send emergency alerts.')) return;
    try {
      const res = await api.post('/automation-rules/emergency/trigger');
      alert(`Emergency Protocol Activated!\n\n${res.data.actions?.map(a => `${a.action}: ${a.status}`).join('\n')}`);
      fetchOverview();
    } catch (e) { alert('Emergency trigger failed: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="control-center-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Control Center</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {overview?.total_devices || 0} devices across {overview?.rooms?.length || 0} rooms
          </p>
        </div>
        <button onClick={triggerEmergency} data-testid="emergency-trigger-btn"
          className="px-3 sm:px-5 py-2.5 sm:py-3 rounded-2xl bg-red-600 text-white font-bold text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 hover:bg-red-700 transition-colors animate-pulse hover:animate-none flex-shrink-0">
          <Siren size={18} weight="fill" /> <span className="hidden sm:inline">EMERGENCY</span><span className="sm:hidden">SOS</span>
        </button>
      </div>

      {/* Security Mode Strip */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar" data-testid="security-mode-strip">
        {SECURITY_MODES.map(m => {
          const active = overview?.security_mode === m.id;
          const Icon = m.icon;
          return (
            <button key={m.id} data-testid={`security-mode-${m.id}`}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 transition-all whitespace-nowrap flex-shrink-0 ${active ? 'ring-2 ring-offset-2 ring-offset-background' : 'opacity-60 hover:opacity-100'}`}
              style={{ background: active ? m.color : 'rgba(255,255,255,0.05)', color: active ? '#fff' : m.color, ringColor: m.color }}>
              <Icon size={14} weight={active ? 'fill' : 'regular'} /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Scene Shortcuts */}
      <SceneShortcuts />

      {/* Door Locks */}
      {locks.length > 0 && (
        <div>
          <h2 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-2 sm:mb-3">Door Locks</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="locks-grid">
            {locks.map(lock => {
              const locked = lock.state === 'locked';
              return (
                <div key={lock.lock_id} className="rounded-2xl p-4 flex items-center justify-between"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${locked ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.3)'}` }}>
                  <div className="flex items-center gap-3">
                    {locked ? <Lock size={22} weight="fill" className="text-green-400" /> : <LockOpen size={22} weight="fill" className="text-red-400" />}
                    <div>
                      <p className="text-sm font-semibold">{lock.name}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">{lock.room} — {lock.state}</p>
                    </div>
                  </div>
                  <button onClick={() => toggleLock(lock.lock_id)} data-testid={`lock-toggle-${lock.lock_id}`}
                    disabled={lockLoading === lock.lock_id}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${locked ? 'bg-red-500/15 text-red-400 hover:bg-red-500/25' : 'bg-green-500/15 text-green-400 hover:bg-green-500/25'}`}>
                    {lockLoading === lock.lock_id ? '...' : locked ? 'Unlock' : 'Lock'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Room Grid */}
      <div>
        <h2 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-2 sm:mb-3">Rooms</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4" data-testid="rooms-grid">
          {overview?.rooms?.map(room => (
            <SwipeableCard
              key={room.room_id}
              onSwipeRight={() => fetchRoomDevices(room.room_id)}
              rightLabel="View Devices"
              rightColor="rgba(255,107,53,0.15)"
            >
              <button onClick={() => fetchRoomDevices(room.room_id)}
                data-testid={`room-card-${room.room_id}`}
                className={`w-full rounded-xl sm:rounded-2xl p-3 sm:p-5 text-left transition-all hover:scale-[1.02] ${selectedRoom === room.room_id ? 'ring-2 ring-[#FF6B35]' : ''}`}
                style={{ background: `linear-gradient(135deg, ${room.color}15 0%, ${room.color}05 100%)`, border: `1px solid ${room.color}30` }}>
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center" style={{ background: `${room.color}20` }}>
                    <HouseLine size={18} weight="duotone" style={{ color: room.color }} />
                  </div>
                  <CaretRight size={12} className="text-muted-foreground" />
                </div>
                <p className="font-semibold text-xs sm:text-sm mb-0.5 sm:mb-1">{room.name}</p>
                <div className="flex items-center gap-2 sm:gap-3 text-[9px] sm:text-[10px] text-muted-foreground flex-wrap">
                  {room.sensors > 0 && <span>{room.sensors} sensors</span>}
                  {room.cameras > 0 && <span>{room.cameras} cameras</span>}
                  {room.appliances > 0 && <span>{room.appliances} devices</span>}
                  {room.device_count === 0 && <span>No devices</span>}
                </div>
                <p className="text-[9px] text-neutral-600 mt-2 md:hidden">Swipe right to view</p>
              </button>
            </SwipeableCard>
          ))}
        </div>
      </div>

      {/* Room Detail Panel */}
      {selectedRoom && roomDevices && (
        <div className="rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="room-detail-panel"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="font-heading text-base sm:text-lg font-bold">{roomDevices.room?.name} — Devices</h2>
            <button onClick={() => { setSelectedRoom(null); setRoomDevices(null); }}
              className="p-2 rounded-xl hover:bg-white/5"><X size={16} /></button>
          </div>
          {roomDevices.devices?.length === 0 ? (
            <p className="text-xs sm:text-sm text-muted-foreground py-6 sm:py-8 text-center">No devices in this room. Add sensors, cameras, or appliances to see them here.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              {roomDevices.devices.map(dev => {
                const Icon = DEVICE_ICONS[dev.subtype] || DEVICE_ICONS[dev.type] || Plug;
                const isActive = ['active', 'on', 'triggered', 'locked'].includes(dev.state);
                return (
                  <div key={dev.id} className="rounded-xl p-4 flex items-center gap-3"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-[#FF6B35]/15' : 'bg-white/5'}`}>
                      <Icon size={20} weight="duotone" className={isActive ? 'text-[#FF6B35]' : 'text-neutral-500'} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{dev.name}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">{dev.type} — {dev.state}</p>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-neutral-600'}`} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
